import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import { fetchOrders } from '../../store/slices/ordersSlice';
import { getPointsBalance } from '../../store/slices/pointsSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import {
  formatOrderStatusLabel,
  getOrderDisplayId,
  getOrderStatusBadgeColor,
} from '../../utils/orderStatusDisplay';
import { getItemPublicPath } from '../../utils/itemLinks';
import { POST_CHECKOUT_SELLER_CATALOG_KEY } from '../../utils/postCheckoutSellerCatalog';

const OrdersContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const ViewHistoryButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const OrderInfo = styled.div`
  flex: 1;
`;

const OrderId = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const OrderDate = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const OrderStatus = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${props => getOrderStatusBadgeColor(props.status)};
  color: white;
`;

const OrderTotal = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
  text-align: right;
  
  @media (max-width: 768px) {
    text-align: left;
    margin-top: 0.5rem;
  }
`;

const PointsTransaction = styled.div`
  background-color: var(--bg-secondary);
  border-radius: 0.375rem;
  padding: 1rem;
  margin-top: 1rem;
`;

const PointsTransactionTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PointsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border-color);
    font-weight: 600;
  }
`;

const PointsLabel = styled.span`
  color: var(--text-secondary);
`;

const PointsValue = styled.span`
  color: var(--text-primary);
  font-weight: ${props => props.bold ? '700' : '600'};
  
  &.deduct {
    color: var(--error-color);
  }
  
  &.add {
    color: var(--success-color);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.45;
  max-width: 28rem;
  margin: 0 auto 1.75rem;
  color: var(--text-primary);
`;

const EmptyBrowseButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary-color);
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

const SellerRecoSection = styled.section`
  margin-bottom: 2rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
`;

const SellerRecoHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const SellerRecoTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
`;

const SellerRecoNote = styled.p`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0.35rem 0 0;
  line-height: 1.45;
`;

const SellerRecoStrip = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  margin-top: 1rem;
`;

const SellerRecoCard = styled(Link)`
  flex: 0 0 140px;
  background: var(--bg-primary);
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid var(--border-color);
  text-decoration: none;
  color: inherit;
`;

const SellerRecoImg = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  background: var(--bg-secondary);
`;

const SellerRecoCardMeta = styled.div`
  padding: 0.5rem;
`;

const SellerRecoCardTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SellerRecoPrice = styled.div`
  font-size: 0.8125rem;
  margin-top: 0.25rem;
  color: var(--primary-color);
  font-weight: 700;
`;

const SellerRecoDismiss = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0.25rem;
`;

const SellerSubheading = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 1.25rem 0 0.5rem;
  color: var(--text-primary);

  &:first-of-type {
    margin-top: 0;
  }
`;

const OrdersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, loading, error } = useSelector((state) => state.orders);
  const { balance } = useSelector((state) => state.points);
  const [sellerReco, setSellerReco] = useState(null);

  useEffect(() => {
    dispatch(fetchOrders());
    dispatch(getPointsBalance());
  }, [dispatch]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(POST_CHECKOUT_SELLER_CATALOG_KEY);
      if (!raw) return;
      sessionStorage.removeItem(POST_CHECKOUT_SELLER_CATALOG_KEY);
      const data = JSON.parse(raw);
      if (data?.sellers?.length) setSellerReco(data);
    } catch (_e) {
      sessionStorage.removeItem(POST_CHECKOUT_SELLER_CATALOG_KEY);
    }
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateBalanceBefore = (order) => {
    // Calculate balance before this order
    // If order has buyer_new_balance, we can calculate it
    if (order.buyer_new_balance !== undefined && order.points_deducted) {
      return order.buyer_new_balance + order.points_deducted;
    }
    // Otherwise, use current balance + points deducted
    if (order.points_deducted && balance !== null) {
      // This is approximate - we'd need to track it better
      return balance + order.points_deducted;
    }
    return null;
  };

  if (loading && orders.length === 0 && !sellerReco) {
    return <LoadingSpinner text="Loading orders..." />;
  }

  return (
    <OrdersContainer>
      <PageHeader>
        <PageTitle>Orders</PageTitle>
        <ViewHistoryButton onClick={() => navigate('/orders')}>
          View Order History
        </ViewHistoryButton>
      </PageHeader>

      {sellerReco && (
        <SellerRecoSection>
          <SellerRecoHead>
            <div>
              <SellerRecoTitle>More from sellers you just bought from</SellerRecoTitle>
              <SellerRecoNote>{sellerReco.deliveryNote}</SellerRecoNote>
            </div>
            <SellerRecoDismiss type="button" aria-label="Dismiss recommendations" onClick={() => setSellerReco(null)}>
              ×
            </SellerRecoDismiss>
          </SellerRecoHead>
          {sellerReco.sellers.map((block) => (
            <div key={block.sellerId}>
              <SellerSubheading>{block.sellerLabel}</SellerSubheading>
              <SellerRecoStrip>
                {block.items.map((it) => (
                  <SellerRecoCard key={it.id} to={getItemPublicPath(it)} state={{ itemId: it.id }}>
                    {it.primary_image ? (
                      <SellerRecoImg src={it.primary_image} alt="" loading="lazy" />
                    ) : (
                      <SellerRecoImg alt="" />
                    )}
                    <SellerRecoCardMeta>
                      <SellerRecoCardTitle>{it.title}</SellerRecoCardTitle>
                      <SellerRecoPrice>{formatItemPriceEgp(it.price)}</SellerRecoPrice>
                    </SellerRecoCardMeta>
                  </SellerRecoCard>
                ))}
              </SellerRecoStrip>
            </div>
          ))}
        </SellerRecoSection>
      )}

      {orders.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📦</EmptyIcon>
          <EmptyTitle>
            When you buy or sell an item, your orders will appear here.
          </EmptyTitle>
          <EmptyBrowseButton to="/pre-owned-items">Browse Items</EmptyBrowseButton>
        </EmptyState>
      ) : (
        <OrdersList>
          {orders.map((order) => {
            const balanceBefore = calculateBalanceBefore(order);
            const balanceAfter = order.buyer_new_balance || (balanceBefore && order.points_deducted ? balanceBefore - order.points_deducted : null);
            const orderRef = String(order.order_number || '').trim();
            const hasPublicOrderRef = orderRef.toUpperCase().startsWith('ORD-');

            return (
              <OrderCard 
                key={order.order_id || order.id}
                as={hasPublicOrderRef ? Link : 'div'}
                to={hasPublicOrderRef ? `/orders/${orderRef}` : undefined}
                style={{ textDecoration: 'none', cursor: hasPublicOrderRef ? 'pointer' : 'default' }}
              >
                <OrderHeader>
                  <OrderInfo>
                    <OrderId>{getOrderDisplayId(order)}</OrderId>
                    <OrderDate>{formatDate(order.created_at)}</OrderDate>
                    <OrderStatus status={order.status || 'pending'}>
                      {formatOrderStatusLabel(order.status)}
                    </OrderStatus>
                  </OrderInfo>
                  <OrderTotal>{formatItemPriceEgp(order.total_amount)}</OrderTotal>
                </OrderHeader>

                {(balanceBefore !== null || balanceAfter !== null) && (
                  <PointsTransaction>
                    <PointsTransactionTitle>Points Transaction</PointsTransactionTitle>
                    {balanceBefore !== null && (
                      <PointsRow>
                        <PointsLabel>Balance Before:</PointsLabel>
                        <PointsValue>🪙 {balanceBefore.toFixed(0)} points</PointsValue>
                      </PointsRow>
                    )}
                    {order.points_deducted && (
                      <PointsRow>
                        <PointsLabel>Amount Deducted:</PointsLabel>
                        <PointsValue className="deduct">-{parseFloat(order.points_deducted).toFixed(0)} points</PointsValue>
                      </PointsRow>
                    )}
                    {balanceAfter !== null && (
                      <PointsRow>
                        <PointsLabel>Balance After:</PointsLabel>
                        <PointsValue bold>🪙 {balanceAfter.toFixed(0)} points</PointsValue>
                      </PointsRow>
                    )}
                  </PointsTransaction>
                )}
              </OrderCard>
            );
          })}
        </OrdersList>
      )}
    </OrdersContainer>
  );
};

export default OrdersPage;
